import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';

/**
 * Seccion Clientes rehecha: actividad y retencion, "dejaron de venir"
 * (accionable, con valor historico y WhatsApp), ranking por valor de vida,
 * y resumen de cobranzas (aging).
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

function DeltaChip({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const color = value > 0 ? 'text-green-600 bg-green-500/10' : value < 0 ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground bg-muted';
  return <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>{value > 0 ? '▲ +' : value < 0 ? '▼ ' : ''}{value}% vs per. ant.</span>;
}

const waLink = (phone: string, name: string) => {
  const p = String(phone || '').replace(/\D/g, '');
  if (!p) return null;
  const full = p.startsWith('54') ? p : '54' + p;
  const msg = encodeURIComponent(`Hola ${name}! Te escribimos de ROCAM. Hace un tiempo que no te vemos por el taller, ¿como anda el vehiculo?`);
  return `https://wa.me/${full}?text=${msg}`;
};

export default function ClientesSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [cobrar, setCobrar] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    Promise.all([
      axios.get('/api/reports/tablero/clientes', { params: { startDate, endDate } }),
      axios.get('/api/reports/tablero/cobrar').catch(() => null)
    ])
      .then(([r1, r2]) => { setD(r1.data); if (r2) setCobrar(r2.data); })
      .catch((e) => setError(e.response?.data?.error || 'Error cargando clientes'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando clientes...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Clientes activos (período)</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.activos.value}</p>
          <DeltaChip value={d.kpis.activos.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Nuevos en el período</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.nuevos.value}</p>
          <DeltaChip value={d.kpis.nuevos.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Recurrentes</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.recurrentes.pct}%</p>
          <span className="text-xs text-muted-foreground">{d.kpis.recurrentes.value} ya habían comprado</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Base histórica</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.base.value}</p>
          <span className="text-xs text-muted-foreground">clientes con compras cargadas</span>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1">Nuevos vs recurrentes por mes</h3>
            <p className="text-xs text-muted-foreground mb-3">{d.coverageNote}</p>
            <ChartContainer
              config={{
                recurrentes: { label: 'Recurrentes', color: 'hsl(var(--chart-2))' },
                nuevos: { label: 'Nuevos', color: 'hsl(var(--chart-1))' }
              }}
              className="h-[220px] w-full"
            >
              <BarChart data={d.serie} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} width={30} allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="recurrentes" stackId="a" fill="var(--color-recurrentes)" radius={[0, 0, 0, 0]} maxBarSize={38} />
                <Bar dataKey="nuevos" stackId="a" fill="var(--color-nuevos)" radius={[3, 3, 0, 0]} maxBarSize={38} />
              </BarChart>
            </ChartContainer>
            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
              <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: '#1baf7a' }} />Recurrentes</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: '#B4B2A9' }} />Nuevos</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Ranking histórico (valor de vida)</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground text-left">
                  <th className="pb-1.5 font-normal">Cliente</th>
                  <th className="pb-1.5 font-normal text-right">Total</th>
                  <th className="pb-1.5 font-normal text-right">Compras</th>
                  <th className="pb-1.5 font-normal text-right">Última</th>
                </tr>
              </thead>
              <tbody>
                {d.ranking.map((c: any) => (
                  <tr key={c.id} className="border-t">
                    <td className="py-1.5 pr-2 truncate max-w-[170px]">{c.name}</td>
                    <td className="py-1.5 pr-2 text-right font-medium">{money(c.revenue)}</td>
                    <td className="py-1.5 pr-2 text-right text-muted-foreground">{c.compras}</td>
                    <td className={`py-1.5 text-right ${c.diasSinVenir > 60 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      {c.diasSinVenir === 0 ? 'hoy' : `hace ${c.diasSinVenir} días`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="font-semibold text-sm">Dejaron de venir</h3>
            <span className="text-xs text-muted-foreground">45+ días sin comprar, ordenados por lo que valen</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">La lista para levantar el teléfono: clientes valiosos que se enfriaron.</p>
          {d.dejaronDeVenir.length === 0 && <p className="text-sm text-muted-foreground">Ningún cliente valioso lleva más de 45 días sin venir. 🎉</p>}
          {d.dejaronDeVenir.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Cliente</th>
                    <th className="pb-1.5 font-normal text-right">Valor histórico</th>
                    <th className="pb-1.5 font-normal text-right">Compras</th>
                    <th className="pb-1.5 font-normal text-right">Sin venir hace</th>
                    <th className="pb-1.5 font-normal text-right">Contacto</th>
                  </tr>
                </thead>
                <tbody>
                  {d.dejaronDeVenir.map((c: any) => {
                    const wa = c.phone ? waLink(c.phone, c.name.split(' ')[0]) : null;
                    return (
                      <tr key={c.id} className="border-t">
                        <td className="py-1.5 pr-2 truncate max-w-[220px]">{c.name}</td>
                        <td className="py-1.5 pr-2 text-right font-medium">{money(c.revenue)}</td>
                        <td className="py-1.5 pr-2 text-right text-muted-foreground">{c.compras}</td>
                        <td className={`py-1.5 pr-2 text-right ${c.diasSinVenir > 90 ? 'text-red-600' : 'text-amber-600'}`}>{c.diasSinVenir} días</td>
                        <td className="py-1.5 text-right">
                          {wa ? (
                            <a href={wa} target="_blank" rel="noreferrer" className="text-green-600 hover:underline text-xs">WhatsApp →</a>
                          ) : (
                            <span className="text-xs text-muted-foreground">sin teléfono</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {cobrar && (
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-semibold text-sm">Cobranzas — {money(cobrar.total)} en {cobrar.deudores} cuentas</h3>
              <span className="text-xs text-muted-foreground">detalle completo en Resumen → Por cobrar</span>
            </div>
            <div className="flex w-full h-3 rounded overflow-hidden mb-2">
              {[['b0_30', '#1baf7a'], ['b31_60', '#EF9F27'], ['b61_90', '#D85A30'], ['b90', '#E24B4A']].map(([k, color]) => {
                const total = Object.values(cobrar.aging as Record<string, number>).reduce((a, b) => a + b, 0) || 1;
                const w = ((cobrar.aging[k as string] || 0) / total) * 100;
                return w > 0 ? <div key={k as string} style={{ width: `${w}%`, background: color as string }} /> : null;
              })}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {cobrar.clientes.slice(0, 3).map((c: any) => (
                <span key={c.clientId}>{c.name.split(' ').slice(0, 2).join(' ')}: <b className="font-medium text-foreground">{money(c.balance)}</b></span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
